<template>
  <v-app>
    <div style="position: absolute; width: 100%; z-index: 99999;">
      <v-progress-linear
        v-if="navigating"
        indeterminate
        color="primary">
      </v-progress-linear>
    </div>

    <main-navigation
      v-if="showNavigation"
      :is-mobile="isMobileApp"
    />
    <v-main :class="mainClasses">
      <div class="kt-grid kt-grid--hor kt-grid--root">
        <div class="kt-grid__item kt-grid__item--fluid kt-grid kt-grid--ver kt-page" :style="{ 'padding: 0px 12px !important;' : !blank }">
          <div class="kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor kt-wrapper" :class="ktWrapperClasses" id="kt_wrapper">
            <HJPlusUpgradeBanner v-if="showNavigation" />
            <div class="kt-container  kt-grid__item kt-grid__item--fluid" id="pageContent">
              <div class="row">
                <div class="col-lg-12" :class="$vuetify.breakpoint.mdAndDown ? 'pa-0' : 'px-3'">
                  <router-view
                      :key="$route.fullPath"
                      :branding-url="brandingUrl"
                      :is-mobile="isMobileApp"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-main>
    <progress-footer v-if="!blank" />
    <reward-unlocked-dialog />
    <welcome-video-modal v-if="currentUser && currentUser.id" />
  </v-app>
</template>

<script>
import { Capacitor } from '@capacitor/core'
import { ScreenOrientation } from '@capacitor/screen-orientation'
import getCurrentUserX from '@/mixins/getCurrentUserX';
import MainNavigation from './components/Navigation/MainNavigation.vue'
import HJPlusUpgradeBanner from './components/HouseJet/HJPlusUpgradeBanner.vue'
import {mapFields} from "vuex-map-fields"
import ProgressFooter from "@/components/ProgressFooter/ProgressFooter.vue"
import RewardUnlockedDialog from "@/components/Onboarding/RewardUnlockedDialog.vue"
import { FeatureFlags } from '@/options/featureFlags'
const WelcomeVideoModal = () => import(/* webpackChunkName: "welcome-video-modal" */ "@/components/Agents/WelcomeVideoModal.vue")
import Clarity from '@microsoft/clarity'
import { Userpilot } from "userpilot";

export default {
  name: 'App',
  components: {MainNavigation, HJPlusUpgradeBanner, ProgressFooter, RewardUnlockedDialog, WelcomeVideoModal},
  mixins:[getCurrentUserX],
  data() {
    return {
      brandingUrl: '',
      updateCurrentUserInterval: false,
    }
  },
  computed: {
    ...mapFields('navigation', ['navigating']),
    isMobileApp() {
      return window.IS_MOBILE_APP || false;
    },
    showNavigation(){
      if (!this.currentUser || !this.currentUser.id) {
        return false
      }
      const noNavRoutes = ['prelogout', 'ppc-register-new', 'ppc-register-precheck', 'mobile-login', 'onboardingFlow', 'onboardingFlow2', 'onboardingFlow3', 'login', 'automation-edit'];
      return !noNavRoutes.includes(this.$route.name);
    },
    blank(){
      const blankPageRoutes = ['prelogout', 'onboardingFlow', 'onboardingFlow2', 'onboardingFlow3', 'login', 'automation-edit'];
      return blankPageRoutes.includes(this.$route.name);
    },
    mainClasses(){
      let cl = [];
      if ( !this.blank ) cl.push('scroll-margin')
      return cl
    },
    ktWrapperClasses(){
      let cl = []
      cl.push(this.blank || !this.showNavigation ? 'no-padding' : 'nav-padding')
      return cl
    }
  },

  watch: {
    currentUser: {
      immediate: true,
      handler(user) {
        this.initUserPilot()
        if (user && user.id && this.isMobileApp) {
          this.loadMobileTheme()
        }
        if (user && user.id && !this.isMobileApp) {
          this.initCircleWidget()
          this.initTawkTo()
          if (process.env.NODE_ENV === 'production') {
            this.initClarity()
          }
        }
      }
    }
  },

  async created() {
    // Initialize any global app state here
    const isMobileApp = Capacitor.isNativePlatform() || window.IS_MOBILE_APP
    if (isMobileApp){
      window.IS_MOBILE_APP = isMobileApp;

      // Enable safe area insets and set user-scalable=no
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', viewport.content + ', user-scalable=no, viewport-fit=cover');
      }

      // Apply mobile app CSS
      const platform = Capacitor.getPlatform();
      if(platform === 'ios'){
        document.body.classList.add('mobile-app-ios');
      } else if(platform === 'android'){
        document.body.classList.add('mobile-app-android');
      } else {
        // Fallback: when loaded via remote URL, getPlatform() may return 'web'
        // but IS_MOBILE_APP is true — apply iOS styles as default
        document.body.classList.add('mobile-app-ios');
      }

      // Lock screen to portrait mode
      try {
        await ScreenOrientation.lock({ orientation: 'portrait' });
      } catch (error) {
        console.error('Failed to lock screen orientation:', error);
      }
    }
    await this.loadMobileTheme();
  },

  async mounted() {
    await this.checkPushNotificationPermission();
    // update the current user every 5 minutes
    if ( !this.updateCurrentUserInterval ){
      this.updateCurrentUserInterval = setInterval(() => {
        console.log('update current user')
        this.getCurrentUser(true)
      }, 300000)
    }
  },

  methods: {
    async loadMobileTheme() {
      try {
        const response = await this.$http.get('/getThemeCss')
        const data = response.data

        // Inject client-specific CSS variables into :root
        if (data.css && this.isMobileApp) {
          const style = document.createElement('style')
          style.id = 'mobile-theme-css'
          style.textContent = `:root { ${data.css} }`
          document.head.insertBefore(style, document.head.firstChild)
        }

        if (data.branding) {
          this.brandingUrl = data.branding?.url
        }
      } catch (error) {
        console.error('[MobileTheme] Failed to load theme CSS:', error)
      }
    },

    initCircleWidget() {

      if (!this.currentUserCanUse(FeatureFlags.KEY_COMMUNITY) || this.isMobileApp ) return
      if (window.__circleWidgetInitialized) return

      window.__circleWidgetInitialized = true

      window.circleWidget = 'mw'
      window.mw = window.mw || function () { (window.mw.q = window.mw.q || []).push(arguments) }
      window.mw.q = window.mw.q || []
      window.mw('init', {
        community_public_uid: 'f631091e',
        brand_color_dark: '#F05F2A',
        brand_color_light: '#f05f2a',
        default_appearance: 'light'
      })

      const script = document.createElement('script')
      script.id = 'circle-widget-script'
      script.src = 'https://community.housejet.com/external/widget.js'
      script.async = true
      document.head.appendChild(script)
    },

    initTawkTo() {
      if (this.platform !== 'ppc' || !this.currentUserCanUse(FeatureFlags.KEY_SUPPORT) || this.isMobileApp ) return
      if (window.__tawkToInitialized) return

      window.__tawkToInitialized = true
      window.Tawk_API = window.Tawk_API || {}
      window.Tawk_LoadStart = new Date()
      window.Tawk_API.onLoad = () => {
        window.Tawk_API.setAttributes({
          name: this.currentUser.full_name,
          email: this.currentUser.email,
          phone: this.currentUser.c_phone
        }, function (error) { if (error) console.error('Tawk.to setAttributes error:', error) })

        setTimeout(() => {
          const tawkIframe = document.getElementById('tawkchat-container')
          if (tawkIframe) tawkIframe.style.zIndex = '2000000000'
          const chatWidget = document.querySelector('#tawkchat-container iframe')
          if (chatWidget) chatWidget.style.zIndex = '2000000000'
        }, 1000)
      }

      const script = document.createElement('script')
      script.async = true
      script.src = 'https://embed.tawk.to/65afdc2a8d261e1b5f56e697/1hkrf815s'
      script.charset = 'UTF-8'
      script.setAttribute('crossorigin', '*')
      document.head.appendChild(script)
    },
    initClarity() {
      try {
        const currentUserId = this.currentUser?.id
        const currentProductKey = this.currentUser?.product_key
        const fullName = this.currentUser?.full_name ? this.currentUser.full_name : this.currentUser?.first_name + ' ' + this.currentUser?.last_name
        if ( !currentUserId || !currentProductKey) return
        if (window.__clarityInitialized) return                                                                                                                               
        window.__clarityInitialized = true // guard to keep clarity from being initialized multiple times if current user refreshes 
        Clarity.init(process.env.MIX_CLARITY_PROJECT_ID)
        Clarity.identify(String(currentUserId), undefined, undefined, fullName) // id needs to be a string for clarity
        Clarity.setTag('Product_Key', currentProductKey)
        Clarity.setTag('User_Type', this.currentUser?.SuperadminLogged ? 'Manage User' : 'Agent')
      } catch (error) {
        console.error('Clarity failed to initialize:', error);
      }
    },

    async addPushNotificationListeners() {
      if (Capacitor.isNativePlatform() === true) {
        const { PushNotifications } = await import('@capacitor/push-notifications');

        // Listen for registration success
        await PushNotifications.addListener('registration', async (token) => {

          try {
            // Save token to backend
            await this.$http.post('/api/v1/firebase-tokens/add', {
              token: token.value,
              type: 'mob'
            });
          } catch (error) {
            console.error('Error saving push token:', error);
          }
        });

        // Listen for registration errors
        await PushNotifications.addListener('registrationError', (err) => {
          console.error('Registration error: ', err.error);
        });

        // Listen for push notifications received while app is in foreground
        await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          // Show notification to user
          this.$noty.info(notification.body || 'New notification received');
        });

        // Listen for notification taps (user clicked on notification)
        await PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          // Handle notification tap - navigate to relevant page if link is provided
          // Try all possible locations for web/android/apple
          const link = notification.notification?.data?.link || notification.data?.link;
          if (link) {
            this.navigateTo(link);
          }
        });
      }
    },

    async checkPushNotificationPermission() {
      // Check if running on native platform
      if (Capacitor.isNativePlatform() === true) {
        try {
          // Add listeners first
          await this.addPushNotificationListeners();

          const { PushNotifications } = await import('@capacitor/push-notifications');
          const result = await PushNotifications.checkPermissions();

          if (result.receive === 'denied') {
            return;
          } else if (result.receive === 'prompt' || result.receive === 'prompt-with-rationale') {
            // Auto-request permissions on page load
            await this.requestPushNotificationPermission();
          } else if (result.receive === 'granted') {
            // Permission already granted, just register
            await PushNotifications.register();
          }
        } catch (error) {
          this.$noty.error('Failed checking push notification permissions');
        }
      }
    },

    async requestPushNotificationPermission() {
      if (Capacitor.isNativePlatform() === true) {
        try {
          const { PushNotifications } = await import('@capacitor/push-notifications');
          const result = await PushNotifications.requestPermissions();

          if (result.receive === 'granted') {
            this.pushNotificationBlocked = false;
            // Register for push notifications after permission granted
            await PushNotifications.register();
            this.$noty.success('Push notifications enabled!');
          } else if (result.receive === 'denied') {
            this.pushNotificationBlocked = true;
            this.$noty.warning('Push notifications were denied. Enable them in your device settings.');
          }
        } catch (error) {
          this.$noty.error('Failed to request push notification permissions.');
        }
      }
    },

    navigateTo(url) {
      console.log('Navigating to URL from notification:', url);

      // Extract path from full URL or use as-is if already a path
      let path = url;
      if (url.startsWith('http://') || url.startsWith('https://')) {
        try {
          const urlObj = new URL(url);
          path = urlObj.pathname + urlObj.search + urlObj.hash;
        } catch (e) {
          console.error('Invalid URL:', url);
          return;
        }
      }

      // Navigate to the route using the full path (routes are defined with full paths like /agents/userProfile)
      this.$nextTick(() => {
        console.log('Next tick Navigating to path:', path);
        this.$router.push({ path: path });
      });
    },
    initUserPilot(){
      if ( window.userpilot ) return  // if userpilot is already loaded don't
      if ( !process.env.MIX_USERPILOT_TOKEN ) return
      if ( !this.currentUser || !this.currentUser?.house_jet_signup?.house_jet_product ) return
      if ( this.currentUser.SuperadminLogged ) return

      const productVersions = ['pro','plg','lea'];  // product versions we're using UserPilot with
      if ( !productVersions.includes(this.currentUser.house_jet_signup.house_jet_product.version) ) return

      const uid = this.currentUser.site_id + '_' + this.currentUser.id
      Userpilot.initialize(process.env.MIX_USERPILOT_TOKEN)
      Userpilot.identify(uid, {
        name: this.currentUser.full_name,
        email: this.currentUser.email,
        created_at: this.currentUser.created,
        product: this.currentUser.house_jet_signup.house_jet_product.key,
        company: {
          id: uid, // Required, used to identify the company
          name: this.currentUser.full_name,
          created_at: this.currentUser.created
        }
      });
    }
  }
}
</script>

<style>
/* Global app styles */

.scroll-margin {
  margin-bottom: 75px; /* Padding so the page will scroll down enough to get content above onboarding footer */
}

.nav-padding {
  padding-top: 80px !important;
}

.no-padding {
  padding-top: 0px !important;
}

/* Mobile app specific styles */
.mobile-app-ios, .mobile-app-android {
  margin-top: 15px !important;

  #kt_header_menu_wrapper {
    margin-top: 45px !important;
  }

  .kt-header__top--mobile{
    height: 0px !important;
  }

  .mobile-horizontal-navigation{
    top: calc(100vh - 80px) !important;
    height: 80px !important;
    padding: 0px 12px !important;
  }
  .mobile-vertical-navigation{
    top: 0px !important;
    padding-top: 60px !important;
  }
}

.v-tour__target--highlighted {
  box-shadow: 0 0 0 99999px rgba(0,0,0,.4) !important;
}

.v-tour__target--relative {
  border-radius: var(--border-radius-primary);
}

</style>